import React, { useState, useEffect, useRef } from "react";
import removeMd from "remove-markdown";
import ConsoleStore from 'ConsoleStore';

const TextToSpeech = ({ message, isDisable, trackUsageProps }) => {
  const synthRef = useRef(window.speechSynthesis);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPause, setIsPause] = useState(false);

  useEffect(() => {
    const loadVoices = () => {
      const voiceList = window.speechSynthesis.getVoices();
      setVoices(voiceList);
      setSelectedVoice(voiceList.find((v) => v.lang === "en-US") || voiceList[0]);
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;


    const handleBeforeUnload = () => {
      synthRef.current.cancel();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // synthRef.current.cancel();
        // setIsSpeaking(false);
        // Pause only if speaking
        if (synthRef.current.speaking && !synthRef.current.paused) {
          synthRef.current.pause();
        }
      }
      else if (document.visibilityState === "visible" && !isPause) {
        // Resume only if it was paused
        if (synthRef.current.paused) {
          synthRef.current.resume();
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    // Cleanup function: stop speech on unmount or re-render
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPause]);

  useEffect(() => {
    return () => {
      // Cleanup: cancel speech synthesis when component unmounts or isDisable changes
      synthRef.current.cancel();
      setIsSpeaking(false);
      setIsPause(false);
    }
  }, []);

  const speak = () => {
    if (isDisable) {
      return;
    }
    ConsoleStore.trackUsage("SpeakerClicked", "start", trackUsageProps);
    synthRef.current.cancel();
    if (!message) return;

    const plainMessage = removeMd(message)
    const utterance = new SpeechSynthesisUtterance(plainMessage);
    utterance.voice = selectedVoice;
    utterance.rate = rate;
    utterance.pitch = pitch;

    utterance.onend = () => setIsSpeaking(false); // Reset state on end
    utterance.onerror = () => setIsSpeaking(false); // Reset state on error

    setIsSpeaking(true);
    synthRef.current.speak(utterance);
  };

  useEffect(() => {
    if (isDisable) {
      synthRef.current.cancel();
      setIsSpeaking(false);
      setIsPause(false);
    }
  }, [isDisable]);

  const stop = () => {
    if (isDisable) {
      return;
    }
    ConsoleStore.trackUsage("SpeakerClicked", "stop", trackUsageProps);
    synthRef.current.cancel();
    setIsSpeaking(false);
    setIsPause(false);
  }
  const pause = () => {
    if (!isSpeaking || isDisable) {
      return;
    }
    ConsoleStore.trackUsage("SpeakerClicked", "pause", trackUsageProps);
    synthRef.current.pause();
    setIsPause(true);
  }
  const resume = () => {
    if (!isSpeaking || isDisable) {
      return;
    }
    ConsoleStore.trackUsage("SpeakerClicked", "resume", trackUsageProps);
    synthRef.current.resume();
    setIsPause(false);
  }
  return <>
  {isSpeaking ? <ReadStop isDisable={isDisable} onClick={stop} /> : 
  <ReadAloud isDisable={isDisable} onClick={speak} />}
  {isPause ? <ReadResume isDisable={!isSpeaking || isDisable} onClick={resume} />:
    <ReadPause isDisable={!isSpeaking || isDisable} onClick={pause} />}
  </>;
};

export default TextToSpeech;

const ReadStop = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={props.isDisable ? "disable" : 'read-stop'}
    {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM9.5 8.5C8.94772 8.5 8.5 8.94772 8.5 9.5V14.5C8.5 15.0523 8.94772 15.5 9.5 15.5H14.5C15.0523 15.5 15.5 15.0523 15.5 14.5V9.5C15.5 8.94772 15.0523 8.5 14.5 8.5H9.5Z"
    >
    </path>
  </svg>
);

const ReadAloud = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={props.isDisable ? "disable" : 'read-aloud'}
    {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11 4.9099C11 4.47485 10.4828 4.24734 10.1621 4.54132L6.67572 7.7372C6.49129 7.90626 6.25019 8.00005 6 8.00005H4C3.44772 8.00005 3 8.44776 3 9.00005V15C3 15.5523 3.44772 16 4 16H6C6.25019 16 6.49129 16.0938 6.67572 16.2629L10.1621 19.4588C10.4828 19.7527 11 19.5252 11 19.0902V4.9099ZM8.81069 3.06701C10.4142 1.59714 13 2.73463 13 4.9099V19.0902C13 21.2655 10.4142 22.403 8.81069 20.9331L5.61102 18H4C2.34315 18 1 16.6569 1 15V9.00005C1 7.34319 2.34315 6.00005 4 6.00005H5.61102L8.81069 3.06701ZM20.3166 6.35665C20.8019 6.09313 21.409 6.27296 21.6725 6.75833C22.5191 8.3176 22.9996 10.1042 22.9996 12.0001C22.9996 13.8507 22.5418 15.5974 21.7323 17.1302C21.4744 17.6185 20.8695 17.8054 20.3811 17.5475C19.8927 17.2896 19.7059 16.6846 19.9638 16.1962C20.6249 14.9444 20.9996 13.5175 20.9996 12.0001C20.9996 10.4458 20.6064 8.98627 19.9149 7.71262C19.6514 7.22726 19.8312 6.62017 20.3166 6.35665ZM15.7994 7.90049C16.241 7.5688 16.8679 7.65789 17.1995 8.09947C18.0156 9.18593 18.4996 10.5379 18.4996 12.0001C18.4996 13.3127 18.1094 14.5372 17.4385 15.5604C17.1357 16.0222 16.5158 16.1511 16.0539 15.8483C15.5921 15.5455 15.4632 14.9255 15.766 14.4637C16.2298 13.7564 16.4996 12.9113 16.4996 12.0001C16.4996 10.9859 16.1653 10.0526 15.6004 9.30063C15.2687 8.85905 15.3578 8.23218 15.7994 7.90049Z">
    </path>
  </svg>
);

const ReadPause = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={props.isDisable ? "disable" : 'read-stop'}
    {...props}>
    <path d="M6 19h4V5H6zm8-14v14h4V5z"></path>
  </svg>
);

const ReadResume = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    className={props.isDisable ? "disable" : 'read-stop'}
    {...props}>
    <path d="M8 5v14l11-7z"></path>
  </svg>
);